/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / handlers comme U3106 */
/* eslint-disable react-hooks/exhaustive-deps -- effets chargement id */
import React, { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3110ROLES } from "../U3110";
import U3110TaxRateBody from "./form/u3110TaxRate/U3110TaxRateBody";
import { U3110TaxRateFormToolBar } from "./form/u3110TaxRate/U3110TaxRateFormToolBar";
import U3110TaxRateDeleteDlg from "./common/U3110TaxRateDeleteDlg";
import { useU3110Context } from "../U3110Context";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  createEmptyU3110FormRow,
  formToSearchRow,
  luHandleAppiError,
  minSortOrderAmongComponents,
  normalizeU3110TaxRateFromApi,
  type U3110TaxRateFormRow,
  type U3110TaxRateGet,
  type U3110TaxRateSearchRow,
  type U3110TaxRateWithOrg,
} from "../U3110Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";

function normalizeForForm(row: U3110TaxRateGet | U3110TaxRateWithOrg): U3110TaxRateFormRow {
  return normalizeU3110TaxRateFromApi(row);
}

export interface U3110TaxRateFormExtendedHandlers extends U2000FormStateHandlersReturn<U3110TaxRateFormRow> {
  addNewTaxRate: () => void;
  modifyTaxRate: () => void;
  deleteTaxRate: () => Promise<void>;
}

const U3110TaxRateForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3110Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith("fr");
  const listTitle = t("title", { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const validateForm = (row: U3110TaxRateFormRow) => {
    const result: Record<string, string> = {};
    const code = row.code?.trim() ?? "";
    if (!code) {
      result["code"] = t("errCodeRequired", {
        ns: NS,
        defaultValue: isFr ? "Le code est obligatoire." : "Code is required.",
      });
    } else if (code.length > 15) {
      result["code"] = t("errCodeLength", {
        ns: NS,
        defaultValue: isFr ? "Le code est trop long." : "Code is too long.",
      });
    }

    const comps = row.taxRateComponents ?? [];
    if (comps.length < 1) {
      result["taxRateComponents"] = t("errComponentsMin", {
        ns: NS,
        defaultValue: isFr ? "Au moins une composante est requise." : "At least one component is required.",
      });
    }

    const orders = comps.map((c) => c.sortOrder);
    const uniq = new Set(orders);
    if (uniq.size !== orders.length) {
      result["taxRateComponents"] = t("errSortOrderDuplicateClient", {
        ns: NS,
        defaultValue: isFr ? "Chaque ordre d’affichage doit être unique." : "Each sort order must be unique.",
      });
    }

    const minSo = comps.length > 0 ? minSortOrderAmongComponents(comps) : 1;

    comps.forEach((c, i) => {
      const base = `taxRateComponents.${i}`;
      if (!Number.isFinite(c.sortOrder) || c.sortOrder < 1 || !Number.isInteger(c.sortOrder)) {
        result[`${base}.sortOrder`] = t("errCompSortOrderInvalid", { ns: NS });
      }
      if (!Number.isFinite(c.rate)) {
        result[`${base}.rate`] = t("errCompRateInvalid", { ns: NS });
      }
      if (c.sortOrder !== minSo && (c.applicationMode === null || c.applicationMode === undefined)) {
        result[`${base}.applicationMode`] = t("errApplicationModeRequired", { ns: NS });
      }
    });

    return result;
  };

  const u3110BL: U2000FormSBusinessHandlers<U3110TaxRateFormRow> = {
    newRecord: () => createEmptyU3110FormRow(),

    validateForm,

    saveForme: async (row: U3110TaxRateFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3110FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },

    cancelForm: () => {
      const old = u3110FormExtended.getOldData() as U3110TaxRateFormRow | null;
      if (old) {
        u3110FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === "/U3110") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    onLeaveForm: () => {
      navigate("/U3110");
    },
  };

  const u3110FormHandlers = useU2000FormStateHandlers<U3110TaxRateFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3110BL,
  });

  const u3110FormExtended: U3110TaxRateFormExtendedHandlers = {
    ...u3110FormHandlers,
    addNewTaxRate: () => {
      u3110FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyTaxRate: () => {
      u3110FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteTaxRate: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3110TaxRateSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3110TaxRateSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3110TaxRateFormRow>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U3110TaxRateFormRow) => {
      const normalized = normalizeForForm(row as unknown as U3110TaxRateGet);
      gridStateHandlers.setDataById(normalized.id, formToSearchRow(normalized));
      u3110FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const crumbLabel = `${normalized.code}${normalized.labelFr ? ` — ${normalized.labelFr}` : ""}`;
      setLogicalUnitBreadcrumb([
        { href: "/U3110", label: listTitle },
        { label: crumbLabel },
      ]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3110FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3110TaxRateFormRow>({
    doSaveFn: (row: U3110TaxRateFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3110TaxRateFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeForForm(row as unknown as U3110TaxRateWithOrg);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), formToSearchRow(normalized)]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, formToSearchRow(normalized));
      }
      u3110FormExtended.afterSaveData(normalized);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3110") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3110FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3110FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3110FormExtended.getData()],
    doDeleteFn: async (rowId, row) => {
      const r = row as U3110TaxRateFormRow;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(r));
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(t("success0003", { key: (del as U3110TaxRateFormRow).code, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3110", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === "new") {
      u3110FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3110FormExtended.setData(createEmptyU3110FormRow());
      setLogicalUnitBreadcrumb([
        { href: "/U3110", label: listTitle },
        { label: t("breadcrumbNew", { ns: NS }) },
      ]);
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (u3110FormExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    hasRole(U3110ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3110FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3110TaxRateAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3110TaxRateDeleteDlg handleDelete={handleDelete as any} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3110TaxRateFormToolBar formStateHandlers={u3110FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3110FormExtended as any}
      >
        <U3110TaxRateBody formStateHandlers={u3110FormExtended as any} t={t} />

        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3110TaxRateForm;
